/**
 * Activity calendar / year heatmap (T13.1 / T21.5 / ISSUE-073 / ADR-0020).
 *
 * Compact GitHub-style layout: 7 rows (Mon–Sun) × N week columns.
 * ~52 weeks × 10 px cells + 2 px gaps ≈ 624 px wide, 84 px tall — fits in
 * one card height regardless of window size.
 *
 * No streaks (D-07): presence dots only, count copy only. Empty days are
 * neutral — never red or "failure" framing. Colour is never the sole
 * indicator: each cell carries title + aria-label.
 */
import type { ActivityCalendarDay } from '@nutians/shared/schemas';
import { useTranslation } from 'react-i18next';

interface ActivityCalendarProps {
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD
  days: ActivityCalendarDay[];
  totalLogged: number;
}

const DOW_SPARSE = ['M', '', 'W', '', 'F', '', ''] as const;

const CELL = 10; // px
const GAP = 2; // px

function isoToDisplay(date: string, lang: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString(lang, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Day-of-week index Mon=0 … Sun=6 */
function dowMon(isoDate: string): number {
  const dow = new Date(`${isoDate}T12:00:00Z`).getUTCDay();
  return dow === 0 ? 6 : dow - 1;
}

function monthShort(isoDate: string): string {
  return new Date(`${isoDate}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });
}

interface CalendarSlot {
  date: string; // always set — even for out-of-range padding days
  day: ActivityCalendarDay | null;
}

interface Week {
  monday: string; // stable key — ISO date of this week's Monday
  slots: CalendarSlot[]; // 7 items Mon–Sun
  monthLabel: string; // non-empty = first week of a new calendar month
}

function buildWeeks(days: ActivityCalendarDay[]): Week[] {
  const firstDay = days[0];
  if (!firstDay) return [];

  const lastDay = days.at(-1);
  if (!lastDay) return [];

  // Expand to full Mon–Sun week boundaries.
  const startOffset = dowMon(firstDay.date);
  const start = new Date(`${firstDay.date}T12:00:00Z`);
  start.setUTCDate(start.getUTCDate() - startOffset);

  const lastDow = dowMon(lastDay.date);
  const end = new Date(`${lastDay.date}T12:00:00Z`);
  end.setUTCDate(end.getUTCDate() + (lastDow === 6 ? 0 : 6 - lastDow));

  const dayMap = new Map(days.map((d) => [d.date, d]));

  // All calendar slots from start → end (always a multiple of 7).
  const allSlots: CalendarSlot[] = [];
  const cur = new Date(start);
  while (cur <= end) {
    const iso = cur.toISOString().slice(0, 10);
    allSlots.push({ date: iso, day: dayMap.get(iso) ?? null });
    cur.setUTCDate(cur.getUTCDate() + 1);
  }

  const weeks: Week[] = [];
  let prevMonth = -1;

  for (let i = 0; i < allSlots.length; i += 7) {
    const weekSlots = allSlots.slice(i, i + 7) as CalendarSlot[];
    const firstSlot = weekSlots[0];
    if (!firstSlot) continue;
    const monday = firstSlot.date;
    const firstReal = weekSlots.find((s) => s.day !== null);
    let monthLabel = '';
    if (firstReal) {
      const m = new Date(`${firstReal.date}T12:00:00Z`).getUTCMonth();
      if (m !== prevMonth) {
        monthLabel = monthShort(firstReal.date);
        prevMonth = m;
      }
    }
    weeks.push({ monday, slots: weekSlots, monthLabel });
  }
  return weeks;
}

export function ActivityCalendar({ from, to, days, totalLogged }: ActivityCalendarProps) {
  const { t, i18n } = useTranslation('progress');
  const weeks = buildWeeks(days);
  const flatSlots = weeks.flatMap((w) => w.slots);

  return (
    <div>
      {/* Header */}
      <div className="mb-3 flex items-baseline justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
          {t('calendar.title')}
        </p>
        <p className="text-xs text-ink-soft">
          {totalLogged === 1
            ? t('calendar.daySingle', { count: totalLogged })
            : t('calendar.dayPlural', { count: totalLogged })}
        </p>
      </div>

      {/* Heatmap */}
      <div className="overflow-x-auto">
        <div className="flex items-start" style={{ gap: 4 }}>
          {/* Day-of-week label column */}
          <div className="flex shrink-0 flex-col" style={{ gap: GAP, marginTop: CELL + GAP }}>
            {DOW_SPARSE.map((label, i) => (
              <div
                key={label === '' ? `empty-dow-${i}` : label}
                aria-hidden
                style={{ width: 8, height: CELL, lineHeight: `${CELL}px` }}
                className="text-right text-[7px] text-ink-soft"
              >
                {label}
              </div>
            ))}
          </div>

          <div className="flex flex-col">
            {/* Month label row */}
            <div className="flex" style={{ gap: GAP, height: CELL, marginBottom: GAP }}>
              {weeks.map((week) => (
                <div key={week.monday} style={{ width: CELL, flexShrink: 0, position: 'relative' }}>
                  {week.monthLabel && (
                    <span
                      className="whitespace-nowrap text-[7px] leading-none text-ink-soft"
                      style={{ position: 'absolute', top: 0, left: 0, lineHeight: `${CELL}px` }}
                    >
                      {week.monthLabel}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Main grid — grid-auto-flow:column places 7 cells per column */}
            <div
              style={{
                display: 'grid',
                gridTemplateRows: `repeat(7, ${CELL}px)`,
                gridAutoFlow: 'column',
                gridAutoColumns: `${CELL}px`,
                gap: GAP,
              }}
            >
              {flatSlots.map((slot) =>
                slot.day !== null ? (
                  <div
                    key={slot.date}
                    role="img"
                    title={isoToDisplay(slot.date, i18n.language)}
                    aria-label={
                      slot.day.logged
                        ? t('calendar.filledA11y', {
                            date: isoToDisplay(slot.date, i18n.language),
                          })
                        : t('calendar.emptyA11y', {
                            date: isoToDisplay(slot.date, i18n.language),
                          })
                    }
                    className={[
                      'rounded-[2px]',
                      slot.day.logged ? 'bg-leaf' : 'bg-husk dark:bg-ink/10',
                    ].join(' ')}
                  />
                ) : (
                  <div key={`empty-${slot.date}`} aria-hidden />
                ),
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Screen-reader summary */}
      <p className="sr-only">
        {totalLogged === 1
          ? t('calendar.daySingle', { count: totalLogged })
          : t('calendar.dayPlural', { count: totalLogged })}{' '}
        from {from} to {to}.
      </p>
    </div>
  );
}
